/**
 * Analizador SEO y panel de revisión.
 *
 * Muestra hechos verificables sobre el contenido, no un score. Un "80/100" es
 * una promesa que el panel no puede cumplir; en cambio "faltan H2, el lector
 * no tiene por dónde navegar" es una observación útil y honesta.
 */

import { SEO_LIMIT, analyzeSeo, buildToc, renderRichContent } from "@vimos/rich-content";
import type { CheckStatus, RichDoc } from "@vimos/rich-content";

interface SeoPanelProps {
  doc: RichDoc;
  title: string;
  excerpt: string;
  slug: string;
  seoTitle: string;
  seoDescription: string;
  coverImageUrl: string;
  coverImageAlt: string;
  focusKeyword: string;
  secondaryKeywords: string;
}

const STATUS_STYLES: Record<CheckStatus, string> = {
  ok: "border-green-200 bg-green-50 text-green-800",
  warn: "border-gold/50 bg-gold-subtle text-ink",
  error: "border-red-200 bg-red-50 text-red-700",
};

const STATUS_LABELS: Record<CheckStatus, string> = {
  ok: "Listo",
  warn: "Revisar",
  error: "Falta",
};

export function SeoPanel(props: SeoPanelProps) {
  const toc = buildToc(props.doc);
  const { checks, keyword, stats } = analyzeSeo({
    doc: props.doc,
    title: props.title,
    excerpt: props.excerpt,
    slug: props.slug,
    seoTitle: props.seoTitle,
    seoDescription: props.seoDescription,
    coverImageUrl: props.coverImageUrl,
    coverImageAlt: props.coverImageAlt,
    focusKeyword: props.focusKeyword,
    secondaryKeywords: props.secondaryKeywords
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
    toc,
  });

  const warnings = checks.filter((check) => check.status !== "ok").length;

  return (
    <div className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-display text-ink text-lg font-bold">Revision editorial</h2>
        <span className="text-mist font-mono text-xs">
          {stats.words} palabras · ~{stats.minutes} min
        </span>
      </div>

      <p className="text-mist mt-1 text-xs">
        {warnings === 0
          ? "No quedan observaciones sobre el contenido."
          : `${warnings} punto(s) por revisar. Estos son hechos sobre el artículo, no una promesa de posicionamiento.`}
      </p>

      <ul className="mt-4 space-y-2">
        {checks.map((check) => (
          <li key={check.id} className={`rounded-md border px-3 py-2 ${STATUS_STYLES[check.status]}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-body text-sm font-semibold">{check.label}</span>
              <span className="font-mono text-[10px] uppercase opacity-70">
                {STATUS_LABELS[check.status]}
              </span>
            </div>
            <p className="mt-0.5 text-xs opacity-85">{check.detail}</p>
          </li>
        ))}
      </ul>

      {keyword && (
        <div className="border-sand/50 mt-4 rounded-md border bg-[#f7f7f4] p-3">
          <p className="font-body text-ink text-xs font-semibold">Palabra clave: {keyword.keyword}</p>
          <ul className="text-mist mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
            {[
              ["Titulo", keyword.inTitle],
              ["Introduccion", keyword.inIntroduction],
              ["H2/H3", keyword.inHeading],
              ["Contenido", keyword.inContent],
              ["Slug", keyword.inSlug],
              ["Meta title", keyword.inSeoTitle],
              ["Meta description", keyword.inSeoDescription],
            ].map(([label, present]) => (
              <li key={String(label)} className="flex items-center gap-1.5">
                <span aria-hidden="true" className={present ? "text-green-600" : "text-navy/30"}>
                  {present ? "✓" : "–"}
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="border-sand/40 text-mist mt-4 border-t pt-3 text-[11px] leading-relaxed">
        Meta title: hasta {SEO_LIMIT.metaTitleMax} caracteres · Meta description: hasta{" "}
        {SEO_LIMIT.metaDescriptionMax} · Contenido recomendado: {SEO_LIMIT.minWords} palabras. Es una guia de
        redaccion, no una regla de buscador.
      </p>
    </div>
  );
}

/**
 * Vista previa del artículo.
 *
 * Reutiliza el renderer de producción. La diferencia con la página real es
 * un prefijo en los anchors: así el índice del preview no choca con el de la
 * página cuando el autor navega entre ambas.
 */
interface ArticlePreviewProps {
  doc: RichDoc;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  coverImageUrl: string;
  coverImageAlt: string;
  readingMinutes: number;
  faqCount: number;
  cta: { title: string; description: string; label: string; url: string } | null;
  related: { title: string; category: string; href: string }[];
  device: "desktop" | "mobile";
  onDeviceChange: (device: "desktop" | "mobile") => void;
}

const DEVICE_WIDTHS = { desktop: "100%", mobile: "390px" } as const;

export function ArticlePreview(props: ArticlePreviewProps) {
  const rendered = renderRichContent(props.doc, {
    wrapper: null,
    anchorPrefix: "preview-",
    lazyImages: false,
  });

  const dateLabel = new Intl.DateTimeFormat("es-PE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(props.date || Date.now()));

  return (
    <div className="border-sand/40 bg-paper shadow-card overflow-hidden rounded-md border">
      <div className="border-sand/40 flex items-center justify-between border-b bg-[#f7f7f4] px-4 py-2.5">
        <p className="text-mist font-mono text-xs uppercase">Vista previa</p>
        <div className="flex gap-1">
          {(["desktop", "mobile"] as const).map((device) => (
            <button
              key={device}
              type="button"
              onClick={() => props.onDeviceChange(device)}
              aria-pressed={props.device === device}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                props.device === device ? "bg-navy text-white" : "text-mist hover:bg-sand/40"
              }`}
            >
              {device === "desktop" ? "Desktop" : "Mobile"}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[720px] overflow-y-auto bg-white">
        <div style={{ maxWidth: DEVICE_WIDTHS[props.device], marginInline: "auto" }} className="px-5 py-7">
          <p className="text-gold-hover font-mono text-[10px] uppercase">{props.category || "Categoria"}</p>
          <h1 className="font-display text-ink mt-2 text-2xl font-bold leading-tight">
            {props.title || "Titulo del articulo"}
          </h1>
          <p className="text-mist mt-3 text-sm">{props.excerpt || "Extracto del articulo"}</p>
          <p className="border-sand/40 text-mist mt-4 flex flex-wrap gap-2 border-t pt-3 text-[11px]">
            <span>{dateLabel}</span>
            <span aria-hidden="true">·</span>
            <span>{props.readingMinutes} min</span>
            <span aria-hidden="true">·</span>
            <span>{props.author || "Autor"}</span>
            {props.faqCount > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <span>{props.faqCount} FAQ</span>
              </>
            )}
          </p>

          {props.coverImageUrl && (
            <img
              src={props.coverImageUrl}
              alt={props.coverImageAlt || props.title}
              className="mt-4 aspect-[16/7] w-full rounded-lg object-cover"
            />
          )}

          {/* rc-prose: la vista previa debe usar los mismos estilos que la
              página pública (listas con viñetas reales, jerarquía tipográfica). */}
          <div className="rc-prose mt-6" dangerouslySetInnerHTML={{ __html: rendered.html }} />

          {props.cta && (
            <div className="border-sand/40 bg-ink mt-8 rounded-lg border p-5 text-white">
              <p className="font-display text-base font-bold">{props.cta.title}</p>
              {props.cta.description && (
                <p className="mt-1.5 text-xs text-white/70">{props.cta.description}</p>
              )}
              <span className="bg-gold text-navy mt-3 inline-flex h-9 items-center rounded-full px-4 text-xs font-semibold">
                {props.cta.label}
              </span>
            </div>
          )}

          {props.related.length > 0 && (
            <div className="border-sand/40 mt-8 border-t pt-5">
              <p className="text-mist font-mono text-[10px] uppercase">Articulos relacionados</p>
              <ul className="mt-2 space-y-2">
                {props.related.map((item) => (
                  <li key={item.href} className="border-sand/40 rounded-md border p-3">
                    <p className="text-gold-hover font-mono text-[10px] uppercase">{item.category}</p>
                    <p className="text-ink mt-0.5 text-sm font-semibold">{item.title}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
